import { ArrowLeftOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Collapse, Dropdown, List, Menu, message, Popconfirm, Row } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { PriceBookEntryForm } from 'components/molecules/PriceBookEntryForm';
import { Stack } from 'components/UI';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import { FormikProvider, useFormik } from 'formik';
import { PriceBookEntryPayload, priceBookEntryPayloadSchema } from 'models/PriceBookEntry';
import moment from 'moment';
import { formatLongDate } from 'utils/formatters';
import React, { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetBusinessQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { useCreatePriceBookEntryMutation, useDeletePriceBookEntryMutation, useGetPriceBookEntryQuery, useUpdatePriceBookEntryMutation } from 'redux/services/julia/juliaApi';
import { useAppSelector } from 'redux/store';

export const PriceBookEntryDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id: businessId, priceBookEntryId } = useParams();
  const isCreateMode = !priceBookEntryId;
  const { sourceProductNumber } = useAppSelector((state) => state.juliaPriceBookEntryParams);

  const { data: business } = useGetBusinessQuery(businessId as string, { skip: !businessId });

  const { data, isLoading } = useGetPriceBookEntryQuery(
    { dataAreaId: business?.dataAreaId || '', businessId: business?.id || '', priceBookEntryId: priceBookEntryId || '' },
    { skip: isCreateMode || !business || !priceBookEntryId }
  );

  const [createEntry] = useCreatePriceBookEntryMutation();
  const [updateEntry] = useUpdatePriceBookEntryMutation();
  const [deleteEntry, { isLoading: isDeleting }] = useDeletePriceBookEntryMutation();

  const submitSucceededRef = useRef(false);
  const { Panel } = Collapse;

  const formik = useFormik<PriceBookEntryPayload>({
    enableReinitialize: true,
    validationSchema: priceBookEntryPayloadSchema,
    initialValues: {
      productNumber: data?.productNumber ?? (isCreateMode && sourceProductNumber ? sourceProductNumber : ''),
      condition: data?.condition ?? '',
      type: data?.type ?? '',
      listPrice: data?.listPrice ?? 0,
      unitPrice: data?.unitPrice ?? 0,
      discountPercentage: data?.discountPercentage ?? 0,
      effectiveDate: data?.effectiveDate ?? '',
      endDate: data?.endDate ?? null
    },
    onSubmit: async (values) => {
      if (!business) {
        message.error('Business data not available');

        return;
      }
      submitSucceededRef.current = false;

      const payload = {
        ...values,
        effectiveDate: moment(values.effectiveDate).format('YYYY-MM-DD'),
        endDate: values.endDate ? moment(values.endDate).format('YYYY-MM-DD') : null
      };

      try {
        if (isCreateMode) {
          const created = await createEntry({
            dataAreaId: business.dataAreaId,
            businessId: business.id,
            payload
          }).unwrap();

          message.success(`Price book entry ${typeof created === 'string' ? '' : created.productNumber} created!`);
          submitSucceededRef.current = true;
          navigate(`/business/${businessId}/price-book`);
        } else {
          await updateEntry({
            dataAreaId: business.dataAreaId,
            businessId: business.id,
            priceBookEntryId: priceBookEntryId!,
            payload
          }).unwrap();
          message.success('Price book entry updated!');
          submitSucceededRef.current = true;
        }
      } catch (error: any) {
        const fallback = isCreateMode ? 'Failed to create price book entry.' : 'Failed to update price book entry.';
        const errorMessage = typeof error?.data === 'string' ? error.data : fallback;

        message.error(errorMessage, 6);
      }
    }
  });

  const handleBack = (): void => {
    navigate(-1);
  };

  const handleClose = (): void => {
    navigate(`/business/${businessId}/price-book`);
  };

  const handleSave = async (close?: boolean): Promise<void> => {
    await formik.submitForm();
    if (close && submitSucceededRef.current) {
      handleClose();
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!business || !priceBookEntryId) return;

    try {
      await deleteEntry({ dataAreaId: business.dataAreaId, businessId: business.id, priceBookEntryId }).unwrap();
      message.success('Price book entry deleted.');
      handleClose();
    } catch (error: any) {
      const errorMessage = typeof error?.data === 'string' ? error.data : 'Failed to delete price book entry.';

      message.error(errorMessage, 6);
    }
  };

  const menuJSX = (
    <Menu disabledOverflow={true}>
      <Menu.Item key="1" onClick={(): Promise<void> => handleSave(false)}>
        Save
      </Menu.Item>
    </Menu>
  );

  if (!isCreateMode && isLoading) {
    return (
      <Row justify="center" align="middle">
        <Col span={24}>
          <BlockLoader direction="loader loader--slideUp" />
        </Col>
      </Row>
    );
  }

  return (
    <Stack flexDirection="column">
      <Col style={{ padding: 10 }}>
        <Stack justifyContent="space-between">
          <AntPageTitle text={isCreateMode ? 'Create Price Book Entry' : `Edit: ${data?.productNumber ?? ''}`} />
          <Stack>
            <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
              Back
            </Button>
            {!isCreateMode && (
              <Popconfirm title="Are you sure you want to delete this entry?" onConfirm={handleDelete} okText="Delete" okButtonProps={{ danger: true }}>
                <Button danger icon={<DeleteOutlined />} loading={isDeleting}>
                  Delete
                </Button>
              </Popconfirm>
            )}
            <Dropdown.Button overlay={menuJSX} onClick={(): Promise<void> => handleSave(true)} loading={formik.isSubmitting} type="primary" icon={<SaveOutlined />}>
              Save and Exit
            </Dropdown.Button>
          </Stack>
        </Stack>
      </Col>

      <FormikProvider value={formik}>
        <PriceBookEntryForm isDisabled={false} isProductNumberLocked={isCreateMode && !!sourceProductNumber} />
      </FormikProvider>

      {!isCreateMode && (
        <Collapse>
          <Panel style={{ background: '#f0f0f0' }} key="1" header="Record Info">
            <List
              dataSource={[
                `ID: ${priceBookEntryId}`,
                `Created by ${data?.createdByFullName ?? ''} on ${data?.createdDateTime ? formatLongDate(data.createdDateTime) : ''}`,
                `Last modified by ${data?.modifiedByFullName ?? ''} on ${data?.modifiedDateTime ? formatLongDate(data.modifiedDateTime) : ''}`
              ]}
              renderItem={(item): JSX.Element => <List.Item style={{ color: '#999' }}>{item}</List.Item>}
            />
          </Panel>
        </Collapse>
      )}
    </Stack>
  );
};
